import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Loader2, MessageCircle, Info } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useMentorTopics, useMentorTopicSelections, useToggleMentorTopicSelection } from "@/hooks/useMentorTopics";

export function MentorTopicsSection() {
  const { data: topics, isLoading: topicsLoading } = useMentorTopics();
  const { data: selections, isLoading: selectionsLoading } = useMentorTopicSelections();
  const toggleSelection = useToggleMentorTopicSelection();

  const isLoading = topicsLoading || selectionsLoading;
  const selectedTopicIds = new Set(selections?.map(s => s.topic_id) || []);

  const handleToggle = (topicId: string, checked: boolean) => {
    toggleSelection.mutate({ topicId, isSelected: checked });
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageCircle className="h-5 w-5" />
          Mentorship Topics
        </CardTitle>
        <CardDescription>
          Indicate the topics you're open to mentoring on
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert className="bg-muted/50 border-muted">
          <Info className="h-4 w-4" />
          <AlertDescription>
            These topics help entrepreneurs understand what kinds of conversations you may be open to.
            <strong className="block mt-1">Selecting a topic does not create any obligation to respond to requests.</strong>
          </AlertDescription>
        </Alert>

        <div className="grid gap-3 sm:grid-cols-2">
          {topics?.map((topic) => {
            const isSelected = selectedTopicIds.has(topic.id);
            return (
              <div
                key={topic.id}
                className={`flex items-start gap-3 p-3 rounded-lg border transition-colors cursor-pointer ${
                  isSelected 
                    ? "border-primary bg-primary/5" 
                    : "border-border hover:border-muted-foreground/30"
                }`}
                onClick={() => handleToggle(topic.id, !isSelected)}
              >
                <Checkbox
                  id={topic.id}
                  checked={isSelected}
                  onCheckedChange={(checked) => handleToggle(topic.id, checked === true)}
                  disabled={toggleSelection.isPending}
                  className="mt-0.5"
                />
                <div className="flex-1 min-w-0">
                  <Label 
                    htmlFor={topic.id} 
                    className="font-medium cursor-pointer"
                  >
                    {topic.name}
                  </Label>
                  {topic.description && (
                    <p className="text-sm text-muted-foreground mt-0.5">
                      {topic.description}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {selectedTopicIds.size > 0 && (
          <div className="pt-2">
            <p className="text-sm text-muted-foreground mb-2">
              Selected topics ({selectedTopicIds.size}):
            </p>
            <div className="flex flex-wrap gap-2">
              {topics
                ?.filter(t => selectedTopicIds.has(t.id))
                .map(topic => (
                  <Badge key={topic.id} variant="secondary">
                    {topic.name}
                  </Badge>
                ))
              }
            </div>
          </div>
        )}

        {selectedTopicIds.size === 0 && (
          <p className="text-sm text-muted-foreground italic">
            You haven't selected any topics yet. Select topics to receive relevant mentorship requests.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
